import { useCallback, useEffect, useRef, useState } from 'react';
import type { PublicSnapshot } from '../../core/types.ts';

const POLL_MS = 3000;

export interface SnapshotState {
  data: PublicSnapshot | null;
  error: string | null;
  /** True only before the first successful load, so the UI never flickers. */
  loading: boolean;
  /** Consecutive failed polls, used to show a reconnecting banner. */
  failures: number;
  refresh: () => void;
}

export function useSnapshot(): SnapshotState {
  const [data, setData] = useState<PublicSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState(0);
  const [loading, setLoading] = useState(true);
  const inFlight = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    inFlight.current?.abort();
    const ctrl = new AbortController();
    inFlight.current = ctrl;
    try {
      const res = await fetch('/api/state', { signal: ctrl.signal, cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData((await res.json()) as PublicSnapshot);
      setError(null);
      setFailures(0);
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setError(err instanceof Error ? err.message : String(err));
      setFailures((f) => f + 1);
    } finally {
      if (inFlight.current === ctrl) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    let timer: number | undefined;

    const schedule = () => {
      window.clearTimeout(timer);
      // Pause polling in a hidden tab; resume instantly when it comes back.
      if (document.hidden) return;
      timer = window.setTimeout(async () => {
        await load();
        schedule();
      }, POLL_MS);
    };
    schedule();

    const onVisible = () => {
      if (!document.hidden) {
        void load();
        schedule();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      inFlight.current?.abort();
    };
  }, [load]);

  return { data, error, loading, failures, refresh: () => void load() };
}

/** Ticking clock so "12s ago" labels stay honest between polls. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Returns a flash class whenever the tracked number moves. */
export function useFlash(value: number | null | undefined): string {
  const prev = useRef(value);
  const [cls, setCls] = useState('');

  useEffect(() => {
    const before = prev.current;
    prev.current = value;
    if (before === undefined || before === null || value === undefined || value === null) return;
    if (value === before) return;
    setCls(value > before ? 'flash-up' : 'flash-down');
    const id = window.setTimeout(() => setCls(''), 750);
    return () => window.clearTimeout(id);
  }, [value]);

  return cls;
}
